import React, { useState, useEffect, useContext, useCallback } from 'react'
import styles from './SectionDetailView.module.css'
import { AuditConfig } from '../../../global/context/audit-config-context/AuditConfigContext'
import { UserContext } from '../../../base/components/base-container/BaseContainer'
import { STATUS } from '../../../global/types/audit'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Backdrop from '@mui/material/Backdrop'
import { fetchData } from '../../../global/utils/fetch'

// Import custom hooks
import { usePromptManagement } from './hooks/usePromptManagement'
import { useTestResults } from './hooks/useTestResults'
import { useExampleImages } from './hooks/useExampleImages'
import { useModalState } from './hooks/useModalState'

// Import components
import SectionSelection, { AuditType } from './SectionSelection'
import AuditInfoPanel from './audit-info-panel/AuditInfoPanel'
import PromptConfiguration from './prompt-configuration/PromptConfiguration'
import TestResults from './test-results/TestResults'
import ImageLightbox from './modals/ImageLightbox'
import FailedExamplesModal from './modals/FailedExamplesModal'
import LookupAuditModal from './modals/LookupAuditModal'
import CleanlinessRubricPanel from './cleanliness-rubric-panel/CleanlinessRubricPanel'
import { CLEANLINESS_PROMPT_SECTION_KEY } from '../../../global/constants/cleanliness'

interface SectionDetailViewProps {
  auditType: AuditType | null
  sectionKey: string | null
  config: AuditConfig | null
  auditTypes: Array<{ key: AuditType; label: string }>
  sections: Array<{ key: string; title: string }>
  onAuditTypeChange: (auditType: AuditType) => void
  onSectionChange: (sectionKey: string) => void
}

interface AIPrompt {
  objectId: string
  auditType: string
  sectionKey: string
  promptText: string
  modelId: string
  isEnabled: boolean
  auditLog?: Array<{
    timestamp: string
    modifiedBy: string
    promptText: string
  }>
}

function SectionDetailView({
  auditType,
  sectionKey,
  config,
  auditTypes,
  sections,
  onAuditTypeChange,
  onSectionChange,
}: SectionDetailViewProps) {
  const { user } = useContext(UserContext)

  // The van cleanliness rubric (FRON-1601) shares the prompt table but is not
  // an audit section: it has no question, defects, example photos or
  // pass/fail test. Those tools get a null section so they stay idle.
  const isCleanlinessRubric =
    auditType === STATUS.WEEKLY_DVIR &&
    sectionKey === CLEANLINESS_PROMPT_SECTION_KEY
  const auditSectionKey = isCleanlinessRubric ? null : sectionKey

  // Toast notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>(
    'success',
  )

  // Section prompt status for dropdown indicators
  const [sectionPromptStatus, setSectionPromptStatus] = useState<
    Record<string, { hasPrompt: boolean; isEnabled: boolean }>
  >({})

  // Function to refetch prompt status (for dropdown indicators)
  const refreshPromptStatus = useCallback(async () => {
    if (!auditType) {
      setSectionPromptStatus({})
      return
    }

    try {
      const url = `${process.env.REACT_APP_ORIGIN}/audit/ai-prompts?auditType=${auditType}`
      const prompts = await fetchData<AIPrompt[]>({ url, method: 'GET' })

      if (prompts && Array.isArray(prompts)) {
        const statusMap: Record<
          string,
          { hasPrompt: boolean; isEnabled: boolean }
        > = {}
        prompts.forEach((prompt) => {
          statusMap[prompt.sectionKey] = {
            hasPrompt: !!prompt.promptText,
            isEnabled: prompt.isEnabled,
          }
        })
        setSectionPromptStatus(statusMap)
      } else {
        setSectionPromptStatus({})
      }
    } catch (error) {
      console.error('Failed to fetch prompts for audit type:', error)
      setSectionPromptStatus({})
    }
  }, [auditType])

  // Fetch all prompts for the audit type to show indicators
  useEffect(() => {
    refreshPromptStatus()
  }, [refreshPromptStatus])

  // Custom hooks
  const promptManagement = usePromptManagement({
    auditType,
    sectionKey,
    userEmail: user?.email,
    onSaveSuccess: refreshPromptStatus,
  })

  const testResults = useTestResults({
    auditType,
    sectionKey: auditSectionKey,
    promptText: promptManagement.promptText,
    selectedModel: promptManagement.selectedModel,
  })

  const exampleImages = useExampleImages({
    auditType,
    sectionKey: auditSectionKey,
  })

  const modalState = useModalState({
    auditType,
    sectionKey: auditSectionKey,
  })

  // Reset test state when section changes
  useEffect(() => {
    if (
      sectionKey !== testResults.lastTestSection ||
      (testResults.testCount && testResults.testCount !== testResults.lastTestCount)
    ) {
      testResults.resetTestState()
    }
  }, [sectionKey, testResults])

  // Format snake_case to Title Case
  const formatSectionTitle = (key: string): string => {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Helper to format timestamps
  const formatTimestamp = (date: Date | null): string => {
    if (!date) return 'Never'
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (seconds < 60) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  // Get human defects helper
  const getHumanDefects = (humanResponse: any): string => {
    if (!humanResponse || !humanResponse.response) return 'No defects recorded'

    const defects = Object.entries(humanResponse.response)
      .filter(([_, value]) => value === true)
      .map(([key]) => key)

    return defects.length > 0 ? defects.join(', ') : 'No defects'
  }

  // Handle save with toast notifications
  const handleSaveWithToast = async () => {
    const result = await promptManagement.handleSave()
    if (result.success) {
      setToastMessage(result.message || 'Saved successfully')
      setToastSeverity('success')
    } else {
      setToastMessage(result.error || 'Failed to save')
      setToastSeverity('error')
    }
  }

  // Handle toggle with toast notifications
  const handleToggleWithToast = async (newValue: boolean) => {
    const result = await promptManagement.handleToggleEnabled(newValue)
    if (!result.success && result.error) {
      setToastMessage(result.error)
      setToastSeverity('error')
    }
  }

  const question =
    config && sectionKey
      ? config.QUESTIONS[sectionKey] || 'No question available'
      : ''
  const defects = config && sectionKey ? config.DEFECTS[sectionKey] || [] : []
  const sectionTitle = sectionKey ? formatSectionTitle(sectionKey) : ''

  return (
    <div className={styles.container}>
      <div className={styles.topSection}>
        {/* Left Panel - Audit Information */}
        <div className={styles.leftPanel}>
          <SectionSelection
            auditType={auditType}
            sectionKey={sectionKey}
            auditTypes={auditTypes}
            sections={sections}
            sectionPromptStatus={sectionPromptStatus}
            onAuditTypeChange={onAuditTypeChange}
            onSectionChange={onSectionChange}
          />

          {isCleanlinessRubric && !promptManagement.isLoading && (
            <CleanlinessRubricPanel
              onUseBuiltInRubric={promptManagement.setPromptText}
            />
          )}

          {!isCleanlinessRubric &&
            auditType &&
            sectionKey &&
            !promptManagement.isLoading && (
              <AuditInfoPanel
              question={question}
              defects={defects}
              sectionTitle={sectionTitle}
              exampleImages={exampleImages.exampleImages}
              currentImageIndex={exampleImages.currentImageIndex}
              isLoadingFailedExamples={modalState.isLoadingFailedExamples}
              lookupAuditId={modalState.lookupAuditId}
              setLookupAuditId={modalState.setLookupAuditId}
              isLoadingLookup={modalState.isLoadingLookup}
              onPreviousImage={exampleImages.handlePreviousImage}
              onNextImage={exampleImages.handleNextImage}
              onFetchFailedExamples={() =>
                modalState.handleFetchFailedExamples(false)
              }
              onLookupAudit={modalState.handleLookupAudit}
            />
          )}

          {promptManagement.isLoading && (
            <div className={styles.emptyState}>
              <h2>Loading...</h2>
              <p>Fetching section details and example images...</p>
            </div>
          )}
        </div>

        {/* Right Panel - Database Config & Testing */}
        {auditType && sectionKey ? (
          <div className={styles.rightPanel}>
            <PromptConfiguration
              promptText={promptManagement.promptText}
              setPromptText={promptManagement.setPromptText}
              isEnabled={promptManagement.isEnabled}
              isSaving={promptManagement.isSaving}
              existingPrompt={promptManagement.existingPrompt}
              lastSavedToDb={promptManagement.lastSavedToDb}
              lastSavedToLocalStorage={promptManagement.lastSavedToLocalStorage}
              hasUnsavedChanges={promptManagement.hasUnsavedChanges}
              selectedModel={promptManagement.selectedModel}
              setSelectedModel={promptManagement.setSelectedModel}
              savedModel={promptManagement.savedModel}
              lastSavedModelToLocalStorage={
                promptManagement.lastSavedModelToLocalStorage
              }
              isTogglingEnabled={promptManagement.isTogglingEnabled}
              handleSave={handleSaveWithToast}
              handleRevertToSaved={promptManagement.handleRevertToSaved}
              handleToggleEnabled={handleToggleWithToast}
              getSaveButtonText={promptManagement.getSaveButtonText}
              formatTimestamp={formatTimestamp}
            />
          </div>
        ) : null}

        {/* Toast Notification */}
        <Snackbar
          open={!!toastMessage}
          autoHideDuration={4000}
          onClose={() => setToastMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToastMessage(null)}
            severity={toastSeverity}
            variant='filled'
          >
            {toastMessage}
          </Alert>
        </Snackbar>

        {/* Loading Overlay */}
        <Backdrop
          open={testResults.isTestingPrompt}
          sx={{ color: 'var(--text-inverse)', zIndex: 9999 }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
            }}
          >
            <CircularProgress
              color='inherit'
              size={60}
            />
            <div
              style={{ textAlign: 'center', fontSize: '18px', fontWeight: 500 }}
            >
              Testing {testResults.testCount} pass + {testResults.testCount}{' '}
              fail audits
              <br />
              <span style={{ fontSize: '14px', opacity: 0.9 }}>
                Using{' '}
                {promptManagement.selectedModel === 'sonnet'
                  ? 'Claude Sonnet 4.5'
                  : 'Claude Haiku 4.5'}
              </span>
            </div>
          </div>
        </Backdrop>
      </div>

      {/* Test Results Section (the cleanliness rubric has no pass/fail test) */}
      {!isCleanlinessRubric && (
        <TestResults
        testCount={testResults.testCount}
        setTestCount={testResults.setTestCount}
        hasTestedBefore={testResults.hasTestedBefore}
        isTestingPrompt={testResults.isTestingPrompt}
        promptText={promptManagement.promptText}
        testResults={testResults.testResults}
        testSummary={testResults.testSummary}
        tokenSummary={testResults.tokenSummary}
        testDateRange={testResults.testDateRange}
        showResults={testResults.showResults}
        correctedAudits={testResults.correctedAudits}
        resultsRef={testResults.resultsRef}
        onTestPrompt={testResults.handleTestPrompt}
        onImageClick={modalState.setLightboxImage}
        onMarkAICorrect={testResults.handleMarkAICorrect}
        getHumanDefects={getHumanDefects}
      />      )}

      {/* Modals */}
      {modalState.lightboxImage && (
        <ImageLightbox
          imageUrl={modalState.lightboxImage}
          onClose={() => modalState.setLightboxImage(null)}
        />
      )}

      {modalState.lookedUpAudit && (
        <LookupAuditModal
          lookedUpAudit={modalState.lookedUpAudit}
          config={config}
          onClose={() => modalState.setLookedUpAudit(null)}
          onImageClick={modalState.setLightboxImage}
          formatSectionTitle={formatSectionTitle}
          getHumanDefects={getHumanDefects}
        />
      )}

      {modalState.showFailedExamples && (
        <FailedExamplesModal
          failedExamples={modalState.failedExamples}
          isLoadingFailedExamples={modalState.isLoadingFailedExamples}
          failedExamplesExhausted={modalState.failedExamplesExhausted}
          failedExamplesSearchInfo={modalState.failedExamplesSearchInfo}
          onClose={() => modalState.setShowFailedExamples(false)}
          onLoadMore={() => modalState.handleFetchFailedExamples(true)}
          onImageClick={modalState.setLightboxImage}
          getHumanDefects={getHumanDefects}
        />
      )}
    </div>
  )
}

export default SectionDetailView
